import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    payload: Object,
    pdfUrl: String
  }

  static targets = ["exportButton"]

  connect() {
    this.charts = []
    this.renderCharts()
  }

  disconnect() {
    this.charts.forEach(({ chart }) => chart.destroy())
    this.charts = []
  }

  async exportPdf(event) {
    event.preventDefault()
    if (!this.hasPdfUrlValue || !this.hasExportButtonTarget) return

    const button = this.exportButtonTarget
    const originalContent = button.innerHTML
    button.disabled = true
    button.innerHTML = '<i class="fal fa-spinner-third fa-spin me-1"></i> Gerando PDF...'

    try {
      const charts = await this.captureCharts()
      const response = await fetch(this.pdfUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Accept": "application/pdf",
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ charts })
      })

      if (!response.ok) throw new Error(`Falha ao gerar PDF (${response.status})`)

      const blob = await response.blob()
      this.downloadBlob(blob, this.responseFilename(response))
    } catch (error) {
      console.error("Falha ao exportar o Dashboard em PDF", error)
      window.alert("Não foi possível gerar o PDF do Dashboard. Tente novamente.")
    } finally {
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  async captureCharts() {
    const captures = []

    for (const { chart, elementId, renderPromise } of this.charts) {
      await renderPromise
      const { imgURI } = await chart.dataURI({ scale: 1.5 })
      captures.push([elementId, imgURI])
    }

    return Object.fromEntries(captures)
  }

  responseFilename(response) {
    const disposition = response.headers.get("Content-Disposition") || ""
    const match = disposition.match(/filename="?([^";]+)"?/i)
    return match?.[1] || "dashboard-armarios.pdf"
  }

  downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  renderCharts() {
    if (!window.ApexCharts || !this.hasPayloadValue) return

    this.renderLineChart("deliveries-by-day-chart", {
      name: "Entregas",
      categories: this.payloadValue.entregas_por_dia.categories,
      series: this.payloadValue.entregas_por_dia.series,
      color: "#0d6efd"
    })

    this.renderDonutChart("occupancy-chart", {
      labels: this.payloadValue.ocupacao_atual.labels,
      series: this.payloadValue.ocupacao_atual.series,
      colors: ["#20c997", "#ffc107", "#dc3545", "#6c757d"]
    })

    this.renderBarChart("usage-by-hour-chart", {
      name: "Entregas",
      categories: this.payloadValue.uso_por_hora.categories,
      series: this.payloadValue.uso_por_hora.series,
      horizontal: false,
      color: "#198754"
    })

    this.renderBarChart("dwell-chart", {
      name: "Retiradas",
      categories: this.payloadValue.permanencia.categories,
      series: this.payloadValue.permanencia.series,
      horizontal: false,
      color: "#0dcaf0"
    })

    this.renderStackedLockerChart()

    this.renderBarChart("deliveries-by-location-chart", {
      name: "Entregas",
      categories: this.payloadValue.entregas_por_local.categories,
      series: this.payloadValue.entregas_por_local.series,
      horizontal: true,
      color: "#6f42c1"
    })

    this.renderDonutChart("delivery-status-chart", {
      labels: this.payloadValue.status_entregas.labels,
      series: this.payloadValue.status_entregas.series
    })

    this.renderDonutChart("armario-presence-chart", {
      labels: this.payloadValue.armarios_online_offline.labels,
      series: this.payloadValue.armarios_online_offline.series,
      colors: ["#198754", "#6c757d"]
    })
  }

  renderLineChart(elementId, options) {
    this.mount(elementId, {
      chart: { type: "area", height: 300, toolbar: { show: false } },
      series: [{ name: options.name, data: options.series }],
      xaxis: { categories: options.categories },
      colors: [options.color],
      stroke: { curve: "smooth", width: 3 },
      fill: { type: "gradient", gradient: { opacityFrom: 0.35, opacityTo: 0.04 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "rgba(125, 125, 125, .18)" }
    })
  }

  renderDonutChart(elementId, options) {
    this.mount(elementId, {
      chart: { type: "donut", height: 300 },
      series: options.series,
      labels: options.labels,
      colors: options.colors,
      legend: { position: "bottom" },
      dataLabels: {
        enabled: true,
        formatter: (_percent, context) => context.w.config.series[context.seriesIndex]
      },
      tooltip: {
        y: {
          formatter: (value) => `${value} registros`
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: "68%"
          }
        }
      }
    })
  }

  renderBarChart(elementId, options) {
    this.mount(elementId, {
      chart: { type: "bar", height: 300, toolbar: { show: false } },
      series: [{ name: options.name, data: options.series }],
      xaxis: { categories: options.categories },
      colors: [options.color],
      plotOptions: {
        bar: {
          horizontal: options.horizontal,
          borderRadius: 4,
          columnWidth: "55%",
          barHeight: "65%"
        }
      },
      dataLabels: { enabled: false },
      grid: { borderColor: "rgba(125, 125, 125, .18)" }
    })
  }

  renderStackedLockerChart() {
    const data = this.payloadValue.ocupacao_por_armario

    this.mount("locker-occupancy-chart", {
      chart: { type: "bar", height: 330, stacked: true, toolbar: { show: false } },
      series: [
        { name: "Ocupadas", data: data.ocupadas },
        { name: "Livres", data: data.livres }
      ],
      xaxis: { categories: data.categories },
      colors: ["#ffc107", "#20c997"],
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%"
        }
      },
      dataLabels: {
        enabled: true,
        formatter: (value) => (value > 0 ? value : ""),
        style: { colors: ["#212529"] }
      },
      grid: { borderColor: "rgba(125, 125, 125, .18)" },
      tooltip: {
        y: {
          formatter: (value, context) => {
            const percent = data.percentual[context.dataPointIndex]
            if (context.seriesIndex === 0) return `${value} gavetas ocupadas (${percent}% ocupado)`

            return `${value} gavetas livres`
          }
        }
      }
    })
  }

  mount(elementId, options) {
    const element = document.getElementById(elementId)
    if (!element) return

    const chart = new window.ApexCharts(element, {
      noData: { text: "Sem dados no período" },
      ...options,
      chart: {
        ...options.chart,
        id: elementId
      }
    })

    const renderPromise = chart.render()
    this.charts.push({ chart, elementId, renderPromise })
  }
}
